import React from 'react';
import {useRoute} from '@react-navigation/native';
import {
  WithdrawWebBankAccountPage,
  type WithdrawAccount,
  type WithdrawBankAccountFormValues,
} from '@fe-progressive/package-progressive/withdraw';
import {useConfirm} from '@/components/basic/modal';
import Spin from '@/components/basic/spin';
import {BasicObject} from '@/types';
import {goBack} from '@/utils';
import globalStore from '@/services/global.state';
import {BankInfo, delBank, onAddBank, updateBank} from './add-bank-service';
import {useTranslation} from 'react-i18next';
import {sendCode} from '@/common-pages/login/login.service';
import {useWithdrawProgressiveViewportHeightStyle} from '../withdraw/withdraw-progressive-theme';

function mapCardInfoToWithdrawAccount(
  cardInfo?: BasicObject,
): WithdrawAccount | null {
  if (!cardInfo) {
    return null;
  }
  return {
    id: cardInfo.id,
    accountName: cardInfo.accountName,
    accountNumber: cardInfo.accountNumber,
    ifscCode: cardInfo.ifscCode,
    userEmail: cardInfo.userEmail,
    upiId: cardInfo.upiId,
    img: cardInfo.img,
  };
}

function mapValuesToBankInfo(
  values: WithdrawBankAccountFormValues,
  id?: string,
): BankInfo {
  return {
    id,
    cardName: values.cardName,
    cardNumber: values.cardNumber,
    ifsCode: values.ifsCode,
    email: values.email,
    upiId: values.upiId,
    smsCode: values.smsCode,
  };
}

const AddBankNew = () => {
  const {i18n} = useTranslation();
  const viewportHeightStyle = useWithdrawProgressiveViewportHeightStyle();
  const {isFirst, cardInfo, ifscCodeError} = useRoute().params as BasicObject;
  const [loading, setLoading] = React.useState(false);
  const [showIfscError, setShowIfscError] = React.useState(
    Boolean(ifscCodeError),
  );
  const {renderModal: renderConfirmModal, show: confirmShow} = useConfirm(
    i18n.t('label.del'),
  );

  const isEdit = Boolean(cardInfo);
  const isFirstAdd = Boolean(parseInt(isFirst, 10));

  const handleSubmit = async (values: WithdrawBankAccountFormValues) => {
    try {
      setLoading(true);
      setShowIfscError(false);
      const params = mapValuesToBankInfo(values, cardInfo?.id);
      if (isEdit) {
        await updateBank(params);
      } else {
        await onAddBank(params);
      }
      goBack();
    } catch (e: any) {
      if (e.data?.code === 9601) {
        setShowIfscError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!cardInfo?.id) {
      return;
    }
    confirmShow(
      i18n.t('bank-page.del.confirmTitle'),
      i18n.t('bank-page.del.confirmText'),
      async () => {
        try {
          setLoading(true);
          await delBank({id: cardInfo.id});
          goBack();
        } finally {
          setLoading(false);
        }
      },
    );
  };

  const handleSendOtp = async () => {
    const userPhone = globalStore.userInfo?.userPhone;
    if (!userPhone) {
      return;
    }
    await sendCode(userPhone);
  };

  return (
    <div className="overflow-y-auto" style={viewportHeightStyle}>
      <Spin loading={loading}>
        <WithdrawWebBankAccountPage
          mode={isEdit ? 'edit' : 'add'}
          isFirstAdd={isFirstAdd}
          account={mapCardInfoToWithdrawAccount(cardInfo)}
          userPhone={globalStore.userInfo?.userPhone}
          loading={loading}
          ifscCodeError={showIfscError}
          onBack={goBack}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          onSendOtp={handleSendOtp}
        />
      </Spin>
      {renderConfirmModal}
    </div>
  );
};

export default AddBankNew;
